// Fleet-wide history view: aggregate trend charts across every worker
// plus a derived change feed (version installs, API bumps, reachability
// flips). Reached via the Workers | History tab.
//
// All charts and the feed read the SAME `trends`/`events` arrays from a
// single `useFleetHistoryAll` fetch — no duplicate requests. Chart
// sections and feed rows are memoized since the page refetches while the
// range is live. Visuals follow the impeccable laws: section headers
// over nested cards, one restrained accent per event type, ease-out
// reveals, and only opacity/transform animated.

import { Alert, AlertDescription } from "@veil/ui/components/alert";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { Skeleton } from "@veil/ui/components/skeleton";
import { formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import Activity from "lucide-react/dist/esm/icons/activity";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle";
import Boxes from "lucide-react/dist/esm/icons/boxes";
import GitBranch from "lucide-react/dist/esm/icons/git-branch";
import History from "lucide-react/dist/esm/icons/history";
import ServerCog from "lucide-react/dist/esm/icons/server-cog";
import ServerCrash from "lucide-react/dist/esm/icons/server-crash";
import { memo, useMemo } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from "recharts";
import { Link } from "wouter";
import { RangeFilter } from "@/components/workers/range-filter";
import { WorkerViewTabs } from "@/components/workers/worker-view-tabs";
import {
	type FleetHistoryEvent,
	type FleetHistoryEventType,
	useFleetHistoryAll,
} from "@/lib/use-fleet-history-all";
import { useFleetRange } from "@/lib/use-fleet-range";

/** Same HH:MM axis formatting used by the per-host charts in Fleet.tsx. */
function formatHHMM(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
}

interface ChartPoint {
	time: string;
	totalSessions: number;
	reachableHosts: number;
	memUsedGB: number;
	loadAvg1m: number;
}

const TOOLTIP_STYLE = {
	fontSize: 12,
	borderRadius: 6,
	border: "1px solid hsl(var(--border))",
	background: "hsl(var(--popover))",
	color: "hsl(var(--popover-foreground))",
} as const;

const AXIS_TICK = {
	fontSize: 10,
	fill: "hsl(var(--muted-foreground))",
} as const;

function ChartEmpty() {
	return (
		<Empty className="border-0">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<Activity />
				</EmptyMedia>
				<EmptyTitle>No samples in window</EmptyTitle>
				<EmptyDescription>
					Pick a wider range or wait for the next poll.
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

const SessionsChart = memo(function SessionsChart({
	data,
}: {
	data: ChartPoint[];
}) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">
					Concurrent sessions (fleet)
				</CardTitle>
			</CardHeader>
			<CardContent
				className={cn("p-0", data.length === 0 ? "px-6 pb-6" : "pb-3 pr-3")}
			>
				{data.length === 0 ? (
					<ChartEmpty />
				) : (
					<ResponsiveContainer width="100%" height={160}>
						<AreaChart
							data={data}
							margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
						>
							<defs>
								<linearGradient id="fhSessGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
									<stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--border))"
							/>
							<XAxis
								dataKey="time"
								tick={AXIS_TICK}
								interval="preserveStartEnd"
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								tick={AXIS_TICK}
								tickLine={false}
								axisLine={false}
								allowDecimals={false}
							/>
							<RechartsTooltip
								contentStyle={TOOLTIP_STYLE}
								formatter={(v) => [v, "sessions"]}
							/>
							<Area
								type="monotone"
								dataKey="totalSessions"
								stroke="#22c55e"
								strokeWidth={2}
								fill="url(#fhSessGrad)"
								dot={false}
								connectNulls
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</CardContent>
		</Card>
	);
});

const ReachableChart = memo(function ReachableChart({
	data,
}: {
	data: ChartPoint[];
}) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">
					Reachable hosts
				</CardTitle>
			</CardHeader>
			<CardContent
				className={cn("p-0", data.length === 0 ? "px-6 pb-6" : "pb-3 pr-3")}
			>
				{data.length === 0 ? (
					<ChartEmpty />
				) : (
					<ResponsiveContainer width="100%" height={160}>
						<AreaChart
							data={data}
							margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
						>
							<defs>
								<linearGradient id="fhHostGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
									<stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--border))"
							/>
							<XAxis
								dataKey="time"
								tick={AXIS_TICK}
								interval="preserveStartEnd"
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								tick={AXIS_TICK}
								tickLine={false}
								axisLine={false}
								allowDecimals={false}
							/>
							<RechartsTooltip
								contentStyle={TOOLTIP_STYLE}
								formatter={(v) => [v, "hosts"]}
							/>
							<Area
								type="monotone"
								dataKey="reachableHosts"
								stroke="#0ea5e9"
								strokeWidth={2}
								fill="url(#fhHostGrad)"
								dot={false}
								connectNulls
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</CardContent>
		</Card>
	);
});

const LoadMemChart = memo(function LoadMemChart({
	data,
}: {
	data: ChartPoint[];
}) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">
					Load &amp; memory (fleet)
				</CardTitle>
			</CardHeader>
			<CardContent
				className={cn("p-0", data.length === 0 ? "px-6 pb-6" : "pb-3 pr-3")}
			>
				{data.length === 0 ? (
					<ChartEmpty />
				) : (
					<ResponsiveContainer width="100%" height={180}>
						<AreaChart
							data={data}
							margin={{ top: 4, right: 36, left: -20, bottom: 0 }}
						>
							<defs>
								<linearGradient id="fhLoadGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
									<stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
								</linearGradient>
								<linearGradient id="fhMemGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
									<stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--border))"
							/>
							<XAxis
								dataKey="time"
								tick={AXIS_TICK}
								interval="preserveStartEnd"
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								yAxisId="load"
								tick={AXIS_TICK}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								yAxisId="mem"
								orientation="right"
								tick={AXIS_TICK}
								tickLine={false}
								axisLine={false}
							/>
							<RechartsTooltip
								contentStyle={TOOLTIP_STYLE}
								formatter={(v, name) => [
									typeof v === "number"
										? name === "loadAvg1m"
											? v.toFixed(2)
											: `${v.toFixed(1)} GB`
										: v,
									name === "loadAvg1m" ? "load" : "mem",
								]}
							/>
							<Area
								yAxisId="load"
								type="monotone"
								dataKey="loadAvg1m"
								stroke="#f59e0b"
								strokeWidth={2}
								fill="url(#fhLoadGrad)"
								dot={false}
								connectNulls
							/>
							<Area
								yAxisId="mem"
								type="monotone"
								dataKey="memUsedGB"
								stroke="#3b82f6"
								strokeWidth={2}
								fill="url(#fhMemGrad)"
								dot={false}
								connectNulls
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</CardContent>
		</Card>
	);
});

interface EventMeta {
	accent: string;
	label: string;
}

const EVENT_META: Record<FleetHistoryEventType, EventMeta> = {
	version: { accent: "text-blue-600 dark:text-blue-400", label: "Version" },
	api: { accent: "text-violet-600 dark:text-violet-400", label: "API" },
	reachability: {
		accent: "text-amber-600 dark:text-amber-400",
		label: "Reachability",
	},
};

const REACHABILITY_DOWN = /unreachable|down|offline|lost|dead/i;

function eventIcon(event: FleetHistoryEvent) {
	switch (event.type) {
		case "version":
			return Boxes;
		case "api":
			return GitBranch;
		case "reachability":
			return REACHABILITY_DOWN.test(event.summary) ? ServerCrash : ServerCog;
	}
}

const EventRow = memo(function EventRow({
	event,
}: {
	event: FleetHistoryEvent;
}) {
	const meta = EVENT_META[event.type];
	const Icon = eventIcon(event);
	return (
		<div className="flex items-start gap-3 py-2">
			<Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", meta.accent)} />
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
					<Link
						href={`/workers/${encodeURIComponent(event.hostname)}`}
						className="font-medium hover:underline"
					>
						{event.hostname}
					</Link>
					<span className="text-muted-foreground">{event.summary}</span>
				</div>
				<span className="sr-only">{meta.label} event</span>
			</div>
			<time
				dateTime={event.at}
				className="shrink-0 text-xs tabular-nums text-muted-foreground"
			>
				{formatRelative(event.at)}
			</time>
		</div>
	);
});

interface EventGroup {
	key: string;
	header: string;
	events: FleetHistoryEvent[];
}

function startOfDay(d: Date): number {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function dayHeader(iso: string): string {
	const d = new Date(iso);
	const diffDays = Math.round(
		(startOfDay(new Date()) - startOfDay(d)) / 86_400_000,
	);
	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Yesterday";
	return d.toLocaleDateString(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
	});
}

const ChangeFeed = memo(function ChangeFeed({
	events,
}: {
	events: FleetHistoryEvent[];
}) {
	const groups = useMemo<EventGroup[]>(() => {
		const out: EventGroup[] = [];
		let current: EventGroup | null = null;
		for (const ev of events) {
			const key = new Date(ev.at).toDateString();
			if (!current || current.key !== key) {
				current = { key, header: dayHeader(ev.at), events: [] };
				out.push(current);
			}
			current.events.push(ev);
		}
		return out;
	}, [events]);

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">
					Changes
				</CardTitle>
			</CardHeader>
			<CardContent>
				{events.length === 0 ? (
					<Empty className="border-0">
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<History />
							</EmptyMedia>
							<EmptyTitle>No changes in this window</EmptyTitle>
							<EmptyDescription>
								Version installs, API bumps, and reachability flips will appear
								here.
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<div className="space-y-4">
						{groups.map((group) => (
							<div key={group.key}>
								<div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
									{group.header}
								</div>
								<div className="divide-y divide-border/60">
									{group.events.map((ev, i) => (
										<EventRow
											key={`${ev.hostname}-${ev.type}-${ev.at}-${i}`}
											event={ev}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
});

export function WorkersHistory() {
	const { range, setPreset, setCustom } = useFleetRange();
	const history = useFleetHistoryAll(range);
	const data = history.data;

	const trends = data?.trends ?? [];
	const events = data?.events ?? [];
	const aggregates = data?.aggregates;
	const retentionDays = data?.retentionDays;

	const chartData = useMemo<ChartPoint[]>(
		() =>
			trends.map((b) => ({
				time: formatHHMM(b.t),
				totalSessions: b.totalSessions,
				reachableHosts: b.reachableHosts,
				memUsedGB: b.memUsedGB,
				loadAvg1m: b.loadAvg1m,
			})),
		[trends],
	);

	const latestReachable = trends.at(-1)?.reachableHosts ?? null;

	const initialLoading = history.isLoading && !data;

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-4">
						<h1 className="text-2xl font-semibold tracking-tight">
							Fleet history
						</h1>
						<WorkerViewTabs active="history" />
					</div>
					<RangeFilter
						value={range}
						onPreset={setPreset}
						onCustom={setCustom}
					/>
				</div>
				{retentionDays != null && (
					<p className="text-xs text-muted-foreground">
						History is retained for {retentionDays} days.
					</p>
				)}
			</div>

			{history.isError && (
				<Alert variant="destructive">
					<AlertTriangle />
					<AlertDescription>
						{history.error instanceof Error
							? history.error.message
							: "Failed to load fleet history."}
					</AlertDescription>
				</Alert>
			)}

			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
				<span>
					Peak{" "}
					<span className="font-medium tabular-nums text-foreground">
						{aggregates?.peakConcurrent ?? 0}
					</span>{" "}
					concurrent
					{aggregates?.peakConcurrentAt
						? ` · ${formatRelative(aggregates.peakConcurrentAt)}`
						: ""}
				</span>
				<span aria-hidden className="text-border">
					|
				</span>
				<span>
					<span className="font-medium tabular-nums text-foreground">
						{latestReachable ?? "—"}
						{aggregates ? `/${aggregates.hostCount}` : ""}
					</span>{" "}
					hosts reachable
				</span>
				<span aria-hidden className="text-border">
					|
				</span>
				<span>
					<span className="font-medium tabular-nums text-foreground">
						{trends.length}
					</span>{" "}
					samples
				</span>
			</div>

			{initialLoading ? (
				<>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<Skeleton className="h-[208px]" />
						<Skeleton className="h-[208px]" />
					</div>
					<Skeleton className="h-[228px]" />
					<Skeleton className="h-48" />
				</>
			) : (
				<>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<SessionsChart data={chartData} />
						<ReachableChart data={chartData} />
					</div>
					<LoadMemChart data={chartData} />
					<ChangeFeed events={events} />
				</>
			)}
		</div>
	);
}
